import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config/config";
import CockpitShell from "../../components/CockpitShell";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { authHeaders, formatDateTime, statusLabel, wtNavItems } from "./wtShared";

const WtHistory = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Experts4Europe | Wissenstest Ergebnisse";
    (async () => {
      try {
        const res = await axios.get(`${config.API_BASE_URL}/api/wt/me/attempts`, { headers: authHeaders() });
        setRows(res.data?.rows || []);
        setError("");
      } catch (e) {
        setError(e.response?.data?.message || "Ergebnisse konnten nicht geladen werden.");
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      const matchesStatus = statusFilter === "all" ? true : (statusFilter === "done" ? r.status === "submitted" : r.status !== "submitted");
      const matchesQuery = !q || String(r.test_name || "").toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [rows, statusFilter, query]);

  return (
    <CockpitShell
      title="Meine Ergebnisse"
      subtitle="Alle Testlaeufe in einer ruhigen Uebersicht."
      badge="Historie"
      navItems={wtNavItems}
      actions={
        <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={() => navigate("/assessment/wt")}>
          Neuen Test starten
        </Button>
      }
    >
      {error ? <p className="rounded-lg border border-rose-300/30 bg-rose-500/10 p-3 text-sm text-rose-100">{error}</p> : null}

      <Card className="card-e4e">
        <CardHeader><CardTitle className="font-display text-white">Filter</CardTitle></CardHeader>
        <CardContent className="grid gap-2 md:grid-cols-3">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nach Testname suchen" className="border-white/20 bg-slate-950/40 text-white" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="border-white/20 bg-slate-950/40 text-white"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="done">Abgeschlossen</SelectItem>
              <SelectItem value="open">In Bearbeitung</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" onClick={() => { setQuery(""); setStatusFilter("all"); }}>
            Zuruecksetzen
          </Button>
        </CardContent>
      </Card>

      <Card className="card-e4e">
        <CardHeader><CardTitle className="font-display text-white">Testlaeufe</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {filtered.length === 0 ? <p className="text-sm text-slate-300">Noch keine Testlaeufe vorhanden.</p> : null}
          {filtered.map((r) => (
            <div key={r.id} className="rounded-lg border border-white/10 bg-slate-900/40 p-3 text-sm text-slate-200">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-white">{r.test_name || "Wissenstest"} · {formatDateTime(r.started_at)}</p>
                <Badge variant="outline" className="border-white/20 bg-white/5 text-slate-100">{statusLabel(r.status)}</Badge>
              </div>
              <p className="mt-1 text-xs text-slate-300">{r.status === "submitted" ? `Score: ${r.score_percent}%` : "Noch nicht abgegeben"}</p>
              <Button
                className="mt-2 bg-cyan-500 text-slate-950 hover:bg-cyan-300"
                onClick={() => navigate(r.status === "submitted" ? `/assessment/wt/result/${r.id}` : `/assessment/wt/run/${r.id}`)}
              >
                {r.status === "submitted" ? "Ergebnis ansehen" : "Fortsetzen"}
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </CockpitShell>
  );
};

export default WtHistory;
